import React from "react";
import { ModalSettings, withFocusable } from "../../services/visualLibraryService";
import Logo from "../../components/Logo";
import { i18n } from "../../services/i18nService";
import { ButtonStyled } from "./styles/Styled";

const ButtonFocus = withFocusable(ButtonStyled);

/**
 * This component shows a modal on top of the rest of the visual elements of the app. Its visibility is managed through
 * the application state actions showSettingsModal and hideSettingsModal. The component receives a configuration object
 * as a prop and displays and behaves differently depending on the configuration parameters received.
 * @param {Object} modalConfig  Configuration object with parameters that can define the modal's appearance and
 * behavior. These are the possible values it can receive:
 * - modalTitle: The title of the modal in string format (optional)
 * - modalText: The body of the modal in string format.
 * - buttons: An array of objects defining the list of buttons to show. Each button can have the following properties:
 *    > label: The text that will be displayed on the button in string format
 *    > focusPath: A unique string value to serve as the identifier of the button for the focus manager
 *    > buttonHandler: A function that will be executed then the button is selected
 * @return {React.Component}
 */
export default ({ modalConfig }) => {
  const isRTL = i18n.getDirection() === i18n.directions.RIGHT_TO_LEFT;
  return (
    <ModalSettings
      descriptionFontWeight={modalConfig.descriptionFontWeight}
      headingText={modalConfig.modalTitle}
      mainText={modalConfig.modalText}
      headerIcon={<Logo />}
      isRTL={isRTL}
    >
      {modalConfig.buttons &&
        modalConfig.buttons.map((button, i, allButtons) => (
          <ButtonFocus
            label={button.label.toUpperCase()}
            focusPath={button.focusPath}
            dataSnUp={getClosestButtonFocusPath("up", allButtons, i)}
            dataSnDown={getClosestButtonFocusPath("down", allButtons, i)}
            displayBlock={true}
            dataSnLeft={getClosestButtonFocusPath("left", allButtons, i)}
            dataSnRight={getClosestButtonFocusPath("right", allButtons, i)}
            onEnterPress={() => button.buttonHandler()}
            onMouseUp={() => button.buttonHandler()}
            size="medium"
            spacing="xsmall"
            key={button.label}
          />
        ))}
    </ModalSettings>
  );
};

/**
 * For a given button within a list of buttons, this function returns the focus path of the immediately closest button
 * in a given direction. If there is no more buttons in the specified direction, the function will return an empty
 * string, meaning that there is no focusable element in that direction.
 * @param {String} direction  specifies the direction on which the closest button needs to be found. Possible values are
 * "left" or "right".
 * @param {Array} buttonsList The list of buttons to be used to look for the closest item for a given button.
 * @param {Number} index  The index in the array of the button whose closest item in a given direction needs to be
 * found.
 * @return {string} The focus path of the closest button found in the received direction or empty string is no more
 * buttons were present in the mentioned direction.
 */
function getClosestButtonFocusPath(direction, buttonsList, index) {
  const appDirection = i18n.getDirection();
  let newIndex = null;

  const getClosestPreviousButton = () => {
    if (index === 0) {
      return "";
    } else {
      return index - 1;
    }
  };
  const getClosestNextButton = () => {
    if (index === buttonsList.length - 1) {
      return "";
    } else {
      return index + 1;
    }
  };

  if (direction === "up") {
    newIndex = getClosestPreviousButton();
  } else if (direction === "down") {
    newIndex = getClosestNextButton();
  } else {
    if (appDirection === i18n.directions.LEFT_TO_RIGHT) {
      if (direction === "left") {
        newIndex = getClosestPreviousButton();
      } else if (direction === "right") {
        newIndex = getClosestNextButton();
      }
    } else if (appDirection === i18n.directions.RIGHT_TO_LEFT) {
      if (direction === "right") {
        newIndex = getClosestPreviousButton();
      } else if (direction === "left") {
        newIndex = getClosestNextButton();
      }
    }
  }
  return newIndex === "" ? "" : "@" + buttonsList[newIndex].focusPath;
}
